import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Upload, Sparkles, FileText, Loader2, Check, AlertTriangle, Search, X,
} from 'lucide-react';
import { api } from '../../lib/api';
import { displayEmail, isPlaceholderEmail } from '../../lib/contactEmail';
import { useOrg } from '../../hooks/useOrg';

// New case — the way a matter actually arrives: a client and a stack of
// documents. Upload what came in, let the AI read it, review the fields it
// found (each with its source and confidence), fix anything, create.
// Manual entry is always available; the AI just fills the form first.

const inputClass = 'w-full px-3 py-2 rounded-[10px] bg-tertiary border border-theme text-[13px] text-primary-c placeholder:text-muted-c focus:outline-none focus:border-strong transition-all';

const GROUPS = [
  { title: 'The case', keys: [
    ['case_type', 'Case type', 'select', ['auto_mva', 'premises', 'other']],
    ['accident_date', 'Date of injury', 'date'],
    ['accident_state', 'State', 'text'],
    ['accident_location', 'Where it happened', 'text'],
    ['accident_description', 'What happened', 'text'],
    ['injuries', 'Injuries / diagnoses', 'text'],
    ['police_report_number', 'Police report #', 'text'],
    ['patient_role', 'Client was', 'select', ['driver', 'passenger', 'pedestrian', 'cyclist', 'other']],
  ] },
  { title: 'Liability (at-fault side)', keys: [
    ['liability_carrier', 'Carrier', 'text'],
    ['liability_claim_number', 'Claim #', 'text'],
    ['liability_adjuster', 'Adjuster', 'text'],
    ['liability_adjuster_phone', 'Adjuster phone', 'text'],
    ['liability_policy_limits', 'BI limits', 'text'],
  ] },
  { title: 'PIP (no-fault)', keys: [
    ['pip_carrier', 'PIP carrier', 'text'],
    ['pip_claim_number', 'PIP claim #', 'text'],
    ['pip_policy_number', 'Policy #', 'text'],
    ['pip_named_insured', 'Named insured', 'text'],
    ['pip_insured_relationship', 'Client’s relationship to insured', 'select', ['self', 'spouse', 'parent', 'child', 'other']],
    ['pip_limit', 'PIP limit ($)', 'number'],
    ['pip_used', 'PIP paid to date ($)', 'number'],
    ['first_treatment_date', 'First treatment (14-day rule)', 'date'],
  ] },
  { title: 'Where PIP bills go', keys: [
    ['pip_billing_address', 'Claims mailing address', 'text'],
    ['pip_billing_fax', 'Claims fax', 'text'],
    ['pip_payer_id', 'Electronic payer ID', 'text'],
    ['pip_adjuster', 'PIP adjuster', 'text'],
    ['pip_adjuster_phone', 'Adjuster phone', 'text'],
    ['pip_adjuster_email', 'Adjuster email', 'text'],
  ] },
  { title: 'Emergency Medical Condition', keys: [
    ['emc_status', 'EMC status', 'select', ['none', 'pending', 'determined', 'denied']],
    ['emc_provider', 'Determined by', 'text'],
    ['emc_date', 'Determined on', 'date'],
  ] },
  { title: 'Health insurance', keys: [
    ['health_carrier', 'Carrier', 'text'],
    ['health_member_id', 'Member ID', 'text'],
  ] },
];

const LABEL = { auto_mva: 'Auto (MVA)', premises: 'Premises', other: 'Other PI', none: 'None', pending: 'Pending', determined: 'Determined', denied: 'Denied', self: 'Self (policyholder)', spouse: 'Spouse', parent: 'Parent', child: 'Child', driver: 'Driver', passenger: 'Passenger', pedestrian: 'Pedestrian', cyclist: 'Cyclist' };

function Confidence({ f }) {
  if (!f) return null;
  const pct = Math.round((f.confidence || 0) * 100);
  const cls = pct >= 85 ? 'bg-success-soft text-success' : pct >= 60 ? 'bg-warning-soft text-warning' : 'bg-danger-soft text-danger';
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold ${cls}`} title={f.note || ''}>
      {pct}%{f.source ? ` · ${f.source}` : ''}
    </span>
  );
}

export default function NewCasePage() {
  const navigate = useNavigate();
  const { currentOrg } = useOrg();
  const fileRef = useRef(null);
  const [params] = useSearchParams();
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState('');
  const [clientId, setClientId] = useState(params.get('client') || ''); // Contacts → "Start a case" lands here preselected
  const [quickAdd, setQuickAdd] = useState(null); // {first_name,last_name,email,phone} or null
  const [docs, setDocs] = useState([]); // uploaded {id, name}
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [proposal, setProposal] = useState(null); // {fields, summary, read, skipped}
  const [values, setValues] = useState({});
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!currentOrg?.id) return;
    api.getContacts(currentOrg.id).then(setContacts).catch(() => {});
  }, [currentOrg?.id]);

  const filtered = useMemo(() => {
    const t = search.trim().toLowerCase();
    return t ? contacts.filter((c) => `${c.first_name} ${c.last_name}`.toLowerCase().includes(t) || (c.email || '').toLowerCase().includes(t)) : contacts;
  }, [contacts, search]);
  const client = contacts.find((c) => c.id === clientId) || null;

  const ensureClient = async () => {
    if (clientId) return clientId;
    if (quickAdd?.first_name && quickAdd?.last_name) {
      const created = await api.createContact(currentOrg.id, quickAdd);
      const id = created.id || created.user?.id;
      setContacts((prev) => [...prev, { ...quickAdd, email: created.email || quickAdd.email, id }]);
      setClientId(id); setQuickAdd(null);
      return id;
    }
    throw new Error('Pick a client or add one (first and last name)');
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true); setError('');
    try {
      const cid = await ensureClient();
      for (const f of files) {
        const d = await api.uploadDocument(currentOrg.id, f, '', cid);
        setDocs((prev) => [...prev, { id: d.id, name: d.name || f.name }]);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const analyze = async () => {
    setAnalyzing(true); setError('');
    try {
      const cid = await ensureClient();
      const p = await api.analyzeCaseIntake(currentOrg.id, { client_id: cid, document_ids: docs.map((d) => d.id) });
      setProposal(p);
      const v = {};
      for (const [k, f] of Object.entries(p.fields || {})) v[k] = f.value;
      setValues((prev) => ({ ...prev, ...v }));
    } catch (err) {
      setError(err.message);
    } finally {
      setAnalyzing(false);
    }
  };

  const create = async () => {
    setCreating(true); setError('');
    try {
      const cid = await ensureClient();
      const cs = await api.applyCaseIntake(currentOrg.id, { client_id: cid, fields: values, document_ids: docs.map((d) => d.id), ai: !!proposal });
      navigate(`/app/cases/${cs.id}`);
    } catch (err) {
      setError(err.message);
      setCreating(false);
    }
  };

  const set = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));
  const clientReady = !!(clientId || (quickAdd?.first_name && quickAdd?.last_name));
  const detailsReady = !!(values.case_type || values.accident_date);
  const canCreate = clientReady && detailsReady;
  // Say what is missing instead of a silently greyed-out button.
  const missing = !clientReady
    ? (quickAdd ? `New client needs ${[!quickAdd.first_name && 'first name', !quickAdd.last_name && 'last name'].filter(Boolean).join(' and ')}` : 'Pick a client or add a new one')
    : !detailsReady ? 'Add a case type or accident date' : '';

  return (
    <div className="max-w-[900px] mx-auto px-4 md:px-7 py-6">
      <Link to="/app/cases" className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-muted-c hover:text-accent-light mb-4">
        <ArrowLeft size={13} /> Cases
      </Link>
      <h1 className="text-[22px] font-bold text-primary-c">New case</h1>
      <p className="text-[13px] text-muted-c mt-1 mb-6 max-w-[560px]">
        Pick the client, upload whatever came in — crash report, ER records, dec page, PIP log —
        and let the AI fill the case from the documents. Review, correct, create.
      </p>

      {error && <div className="text-[12px] text-red-400 bg-red-500/10 border border-red-500/20 rounded-[10px] px-3.5 py-2.5 mb-4">{error}</div>}

      {/* 1 · Client */}
      <section className="bg-elevated border border-theme rounded-[14px] p-5 mb-4">
        <div className="text-[11px] font-bold uppercase tracking-[1px] text-muted-c mb-3">1 · Client</div>
        {client ? (
          <div className="flex items-center gap-3">
            <div className="w-[36px] h-[36px] rounded-[10px] bg-accent-glow flex items-center justify-center text-[11px] font-bold text-accent-light">
              {(client.first_name || '?')[0]}{(client.last_name || '?')[0]}
            </div>
            <div className="flex-1">
              <div className="text-[13.5px] font-semibold text-primary-c">{client.first_name} {client.last_name}</div>
              <div className="text-[11.5px] text-muted-c">{[displayEmail(client.email, 'No email on file'), client.phone].filter(Boolean).join(' · ')}</div>
            </div>
            <button onClick={() => setClientId('')} className="text-[12px] font-semibold text-muted-c hover:text-accent-light">Change</button>
          </div>
        ) : quickAdd ? (
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
            {[['first_name', 'First name'], ['last_name', 'Last name'], ['email', 'Email (optional)'], ['phone', 'Phone']].map(([k, ph]) => (
              <input key={k} placeholder={ph} value={quickAdd[k] || ''} onChange={(e) => setQuickAdd({ ...quickAdd, [k]: e.target.value })} className={inputClass} />
            ))}
            <label className="sm:col-span-4 flex items-start gap-2 cursor-pointer"><input type="checkbox" checked={!!quickAdd.sms_opt_in} onChange={(e) => setQuickAdd({ ...quickAdd, sms_opt_in: e.target.checked })} className="mt-0.5 accent-[#0DA7CA]" /><span className="text-[11.5px] text-muted-c leading-snug">Patient agreed to receive appointment &amp; referral texts at this number. No medical details are texted; STOP opts out.</span></label>
            <button onClick={() => setQuickAdd(null)} className="text-[12px] text-muted-c sm:col-span-4 text-left">Pick an existing client instead</button>
          </div>
        ) : (
          <div>
            <div className="flex items-center gap-2 bg-tertiary border border-theme rounded-[10px] px-3 h-[38px] mb-2">
              <Search size={13} className="text-muted-c" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search clients…" className="flex-1 bg-transparent text-[13px] text-primary-c focus:outline-none" />
            </div>
            <div className="max-h-[180px] overflow-y-auto divide-y divide-theme rounded-[10px] border border-theme">
              {filtered.slice(0, 50).map((c) => (
                <button key={c.id} onClick={() => setClientId(c.id)} className="w-full text-left px-3 py-2 text-[13px] text-primary-c hover:bg-hover-surface flex items-center justify-between">
                  <span>{c.first_name} {c.last_name}</span><span className="text-[11px] text-muted-c">{displayEmail(c.email, c.phone || '')}</span>
                </button>
              ))}
              {filtered.length === 0 && <div className="px-3 py-3 text-[12px] text-muted-c">No matches.</div>}
            </div>
            <button onClick={() => setQuickAdd({ first_name: '', last_name: '', email: '', phone: '', sms_opt_in: false })} className="mt-2 text-[12px] font-semibold text-accent-light">+ New client</button>
          </div>
        )}
      </section>

      {/* 2 · Documents */}
      <section className="bg-elevated border border-theme rounded-[14px] p-5 mb-4">
        <div className="text-[11px] font-bold uppercase tracking-[1px] text-muted-c mb-3">2 · Documents</div>
        <label className={`flex items-center justify-center gap-2 p-6 border-2 border-dashed border-strong rounded-[12px] text-[13px] text-muted-c hover:border-accent-light hover:text-accent-light transition-all cursor-pointer ${uploading ? 'opacity-50' : ''}`}>
          <input ref={fileRef} type="file" multiple accept=".pdf,.txt" onChange={onFiles} disabled={uploading} className="hidden" />
          {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
          {uploading ? 'Uploading…' : 'Drop or choose PDFs — crash report, ER records, dec page, PIP log, letters'}
        </label>
        {docs.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {docs.map((d) => (
              <span key={d.id} className="inline-flex items-center gap-1.5 text-[11px] font-mono text-secondary-c bg-tertiary rounded-md px-2 py-1 max-w-[280px]">
                <FileText size={11} className="shrink-0" /><span className="truncate">{d.name}</span>
                <button onClick={() => setDocs((prev) => prev.filter((x) => x.id !== d.id))} className="text-muted-c hover:text-danger"><X size={11} /></button>
              </span>
            ))}
          </div>
        )}
        <div className="flex items-center gap-3 mt-4 flex-wrap">
          <button
            onClick={analyze}
            disabled={analyzing || docs.length === 0}
            className="inline-flex items-center gap-1.5 px-4 py-[9px] rounded-[10px] text-[12.5px] font-semibold text-white gradient-accent shadow-glow disabled:opacity-50"
          >
            {analyzing ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} {analyzing ? 'Reading the documents…' : 'Fill the case from these documents'}
          </button>
          <span className="text-[11.5px] text-muted-c">Reads PDFs with Claude on AWS (inside your BAA). Nothing is saved until you click Create.</span>
        </div>
        {proposal && (
          <div className="mt-4 rounded-[12px] bg-tertiary p-3.5 text-[12.5px] text-primary-c">
            <div className="flex items-center gap-1.5 font-semibold mb-1"><Check size={13} className="text-success" /> Read {proposal.read?.length || 0} document{proposal.read?.length === 1 ? '' : 's'} · {Object.keys(proposal.fields || {}).length} fields found</div>
            <p className="text-muted-c">{proposal.summary}</p>
            {proposal.skipped?.length > 0 && (
              <p className="text-warning mt-1 flex items-start gap-1"><AlertTriangle size={12} className="mt-0.5" /> Skipped: {proposal.skipped.map((s) => `${s.name} (${s.reason})`).join('; ')}</p>
            )}
          </div>
        )}
      </section>

      {/* 3 · Review */}
      <section className="bg-elevated border border-theme rounded-[14px] p-5 mb-4">
        <div className="text-[11px] font-bold uppercase tracking-[1px] text-muted-c mb-1">3 · Case details</div>
        <p className="text-[12px] text-muted-c mb-4">{proposal ? 'Pre-filled from the documents — each value shows its confidence and source. Fix anything that’s off.' : 'Enter what you know, or upload documents above and let the AI fill this in.'}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          {GROUPS.map((g) => (
            <div key={g.title}>
              <div className="text-[12px] font-bold text-primary-c mb-2">{g.title}</div>
              <div className="flex flex-col gap-2.5">
                {g.keys.map(([k, label, type, opts]) => (
                  <label key={k} className="block">
                    <span className="flex items-center justify-between text-[10.5px] font-semibold text-muted-c mb-1">
                      <span>{label}</span><Confidence f={proposal?.fields?.[k]} />
                    </span>
                    {type === 'select' ? (
                      <select value={values[k] || ''} onChange={(e) => set(k, e.target.value)} className={inputClass}>
                        <option value="">—</option>
                        {opts.map((o) => <option key={o} value={o}>{LABEL[o] || o}</option>)}
                      </select>
                    ) : (
                      <input type={type} value={values[k] || ''} onChange={(e) => set(k, e.target.value)} className={inputClass} maxLength={k === 'accident_state' ? 2 : undefined} />
                    )}
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="flex items-center justify-end gap-3 flex-wrap">
        {missing && <span className="text-[12px] text-warning">{missing}</span>}
        <Link to="/app/cases" className="px-4 py-2.5 rounded-[10px] text-[13px] font-semibold text-primary-c bg-tertiary border border-theme">Cancel</Link>
        <button onClick={create} disabled={!canCreate || creating} className="px-5 py-2.5 rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow disabled:opacity-50 inline-flex items-center gap-1.5">
          {creating ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Create case
        </button>
      </div>
    </div>
  );
}
